import camelize from 'camelize';
import { Component } from 'preact';

import * as EMBED_SETTINGS from '../constants/embed_settings';

const TRACK_SIZE_OFFSET = 60;

class PlaylistContainer extends Component {
  constructor(props) {
    super(props);

    const isLatestClipPlaceholder = props.clips.length > 0 && props.clips[0].isPending;
    const clipsCount = props.clips.length;
    const shouldShowArchivedClip =
      isLatestClipPlaceholder && clipsCount > 1 && props.playlistData.showLatestArchivedClip;
    const selectedClipId = shouldShowArchivedClip ? props.clips[1].id : props.clips[0].id;

    this.state = {
      // todo: Can we avoid 'total' override? Maybe just show clips length for PlaylistPlaybar.jsx?
      playlistData: {
        ...props.playlistData,
        total: shouldShowArchivedClip ? clipsCount - 1 : clipsCount,
      },
      clips: shouldShowArchivedClip ? props.clips.slice(1) : props.clips,
      currentLiveViewers: null,
      isHovered: false,
      isLive: props.clips.length > 0 ? props.clips[0].isLive : false,
      isLoading: false,
      isMobile: true,
      isTiny: false,
      isPlaying: false,
      isShowing: true,
      livePollId: null,
      nextLiveClip: null,
      selectedClipId,
      selectedClipOffsetTop: 0,
      showUpNext: false,
      tracklistExpanded: false,
      upNextClip: props.clips.length > 1 ? props.clips[1] : null,
      hidePlaylistUI: props.hidePlaylistUI,
      isControlBarVisible: true,
      isToastVisible: false,
    };
  }

  componentDidMount = () => {
    // Allows users to preview customizing the player:
    window.addEventListener('message', this.updatePlayerConfig);

    // Custom logo needs to be set on load and on switchClip:
    if (this.state.playlistData.customLogo) {
      this.props.player._setEmbedSetting(
        EMBED_SETTINGS.SHOW_CUSTOM_LOGO,
        this.state.playlistData.customLogo,
      );
    }

    this.props.player.addEventListener('play', () => {
      this.setState((prevState) => ({
        isPlaying: true,
        showUpNext: false,
      }));
    });

    this.props.player.addEventListener('bufferend', () => {
      if (this.state.isPlaying) {
        this.setState((prevState) => ({ isLoading: false }));
      }
    });

    this.props.player.addEventListener('pause', () => {
      this.setState((prevState) => ({ isPlaying: false }));
    });

    // Show UpNext clip on desktop after clip ends, unless autoplay is true
    this.props.player.addEventListener('ended', () => {
      if (this.state.isMobile || this.props.playlistData.autoplay) {
        const { clips, selectedClipId } = this.state;
        const currentIndex = clips.findIndex((e) => e.id === selectedClipId);
        const nextIndex = currentIndex + 1 === clips.length ? 0 : currentIndex + 1;
        // Looping back to first clip does not autoplay, unless `loop` is true:
        const autoplay = nextIndex !== 0 || this.props.playlistData.loop;
        const nextClipId = clips[nextIndex];
        this.switchClip(nextClipId, autoplay, this.state.selectedClipOffsetTop + TRACK_SIZE_OFFSET);
      }
      this.hideTracklist();
      this.setState((prevState) => ({
        isLive: false,
        isPlaying: false,
        showUpNext: !this.props.playlistData.autoplay && !this.state.isMobile,
      }));
    });

    /**
     * Shows/hides the playlist UI based on player controls
     */
    this.props.player.addEventListener('controlbarvisibilitychanged', (data) => {
      this.setState({
        isControlBarVisible: data.visible,
      });
      // Never show playlist if a player toast is displayed
      if (data.visible && !this.state.isShowing && !this.state.isToastVisible) {
        this.show();
      }
      if (!data.visible && this.state.isShowing) {
        // Need to wait 10ms because visibility changes at
        // the exact moment the mouse leaves the player for the playlist UI
        setTimeout(() => {
          if (!this.state.isHovered) {
            this.hide();
          }
        }, 0);
      }
    });

    /**
     * Control playlist UI visibility based on toast visibility
     *
     * @todo This will throw an error if the player does not have the
     * toastvisibilitychanged event defined, hence the try/catch.
     * Once the player version with the event has been rolled out,
     * we are safe to remove the try/catch.
     */
    try {
      this.props.player.addEventListener('toastvisibilitychanged', (data) => {
        this.setState({
          isToastVisible: data.visible,
        });
        if (data.visible && this.state.isShowing) {
          this.hide();
        }
        if (!data.visible && !this.state.isShowing && this.state.isControlBarVisible) {
          this.show();
        }
      });
    } catch (err) {
      // Ignore error if toastvisibilitychanged is not available
    }

    /**
     * Fetch live event data & viewers
     * Probably should not be done more than once every 30 seconds
     */
    const fetchLiveStats = () => {
      /**
       * This URL is being used to cache at fastly, before making changes to the URL structure,
       * open a ticket with SRE: go.vimeows.com/infraticket to update caching
       * for context: https://github.vimeows.com/Vimeo/infra-plans/issues/13362
       */
      const hashParam = this.props.playlistData.unlistedHash
        ? `?h=${this.props.playlistData.unlistedHash}`
        : '';
      const url = `/live_event/${this.props.playlistData.id}/status${hashParam}`;
      fetch(url)
        .then((res) => (res.ok ? Promise.resolve(res) : Promise.reject(res)))
        .then((res) => res.json())
        .then(camelize)
        .then((res) => {
          const { selectedClipId } = this.state;

          if (res.currentLiveViewers) {
            this.setState((prevState) => ({
              currentLiveViewers: res.currentLiveViewers,
            }));
          }

          if (res.nextLiveClip && res.nextLiveClip.id !== selectedClipId) {
            this.setState({ nextLiveClip: res.nextLiveClip });
          }
        });
    };
    if (this.props.isRLE) {
      fetchLiveStats();
      const livePollId = setInterval(fetchLiveStats, 30000);
      this.setState((prevState) => ({
        livePollId,
      }));
    }

    window.addEventListener('resize', this.onResize);
    this.onResize();
  };

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('message', this.updatePlayerConfig);
    this.props.player.removeEventListener('play');
    this.props.player.removeEventListener('pause');
    this.props.player.removeEventListener('ended');
    this.props.player.removeEventListener('bufferend');
    this.props.player.removeEventListener('controlbarvisibilitychanged');
    if (this.state.livePollId) {
      clearInterval(this.state.livePollId);
    }
  };

  componentDidUpdate = (prevProps, prevState) => {
    // Hack to prevent Vimeo logo from trying to go to clip page
    // inside embedded-playlist iframe:
    if (prevState.selectedClipId === this.state.selectedClipId) {
      const logoLink = document.querySelector('.logo a');
      if (logoLink) {
        logoLink.target = '_blank';
        logoLink.addEventListener('click', (e) => {
          e.stopPropagation();
        });
      }
    }

    if (prevState.clips.length !== this.state.clips.length) {
      this.switchClip(this.state.clips[0], false);
    }
  };

  /**
   * Used in the NextLiveClip component
   * Adds the newly-activated live clip to the beginning of the Playlist
   * Updates playlist total count
   */
  addNewLiveClip = (clip) => {
    const clips = [clip, ...this.state.clips];
    const playlistData = Object.assign({}, this.state.playlistData, { total: clips.length });
    this.setState(
      (prevState) => ({
        playlistData,
        clips,
        // Start autoplaying the clip once it's loaded
      }),
      this.switchClip(clip, true),
    );
  };

  toggleShowing = () => {
    this.setState((prevState) => ({ isShowing: !prevState.isShowing }));
  };

  hide = () => {
    this.setState((prevState) => ({ isShowing: false }));
  };

  show = () => {
    this.setState((prevState) => ({ isShowing: true }));
  };

  toggleTracklist = () => {
    this.setState(
      (prevState) => ({ tracklistExpanded: !prevState.tracklistExpanded }),
      () => {
        if (this.state.tracklistExpanded) {
          // Pause on mobile is tracklist is expanded
          if (this.state.isMobile && this.state.isPlaying) {
            this.props.player.pause();
          }

          // Make sure the selected clip is always visible
          if (this.state.selectedClipOffsetTop > this.tracklistRef.clientHeight) {
            this.tracklistRef.scrollTop = this.state.selectedClipOffsetTop;
          }

          // Makes sure the tracklist doesn't expand past playbar
          this.setTracklistMaxHeight();
        } else {
          // Collapse tracklist here because 'maxHeight' is the transition target
          this.tracklistRef.style.maxHeight = 0;
        }
      },
    );
  };

  hideTracklist = () => {
    this.setState((prevState) => ({ tracklistExpanded: false }));
    this.tracklistRef.style.maxHeight = 0;
  };

  onResize = () => {
    const isTiny = window.document.body.offsetWidth <= 300;
    const isMobile = window.document.body.offsetWidth <= 500;

    this.setState((prevState) => ({ isMobile, isTiny }));

    if (this.state.tracklistExpanded) {
      this.setTracklistMaxHeight();
    }
  };

  onMouseEnter = () => {
    this.setState((prevState) => ({ isHovered: true }));
  };

  onMouseLeave = () => {
    this.setState((prevState) => ({ isHovered: false }));
  };

  setTracklistMaxHeight = () => {
    const PLAYBAR_OFFSET = 100;
    this.tracklistRef.style.maxHeight = this.state.isMobile
      ? `${this.props.playerEl.clientHeight - this.headerRef.clientHeight}px`
      : `${this.props.playerEl.clientHeight - this.headerRef.clientHeight - PLAYBAR_OFFSET}px`;
  };

  switchClip = (clip, autoplay = true, offsetTop = 0) => {
    // Pause/play if the user clicks on selected clip:
    if (clip.id === this.state.selectedClipId) {
      if (!clip.isPending) {
        if (this.state.isPlaying) {
          this.props.player.pause();
        } else {
          this.props.player.play();
        }
      }
      return;
    }
    // Set upNextClip:
    const { clips } = this.state;
    const currentIndex = clips.findIndex((e) => e.id === clip.id);
    const nextIndex = currentIndex + 1 === clips.length ? 0 : currentIndex + 1;
    const upNextClip = clips[nextIndex];

    this.setState(
      (prevState) => ({
        isLive: clip.isLive,
        isLoading: autoplay,
        selectedClipId: clip.id,
        selectedClipOffsetTop: offsetTop,
        showUpNext: false,
        upNextClip,
      }),
      // Use callback so clip loads after state updates:
      () => {
        const config = autoplay ? clip.config : clip.configNoAutoplay;
        this.props.player.loadVideo(config).then(() => {
          // Custom logo needs to be set manually:
          if (this.state.playlistData.customLogo) {
            this.props.player._setEmbedSetting(
              EMBED_SETTINGS.SHOW_CUSTOM_LOGO,
              this.state.playlistData.customLogo,
            );
          }
          // todo: Thumbnails load for RLE events?
        });
        if (this.state.isMobile) {
          this.hideTracklist();
        }
      },
    );
  };

  nextClip = () => {
    const { clips, selectedClipId, isPlaying, showUpNext } = this.state;
    const currentIndex = clips.findIndex((e) => e.id === selectedClipId);
    const nextIndex = currentIndex + 1 === clips.length ? 0 : currentIndex + 1;
    const nextClipId = clips[nextIndex];
    const shouldAutoplay = isPlaying || showUpNext;
    this.switchClip(
      nextClipId,
      shouldAutoplay,
      this.state.selectedClipOffsetTop + TRACK_SIZE_OFFSET,
    );
  };

  previousClip = () => {
    const { clips, selectedClipId, isPlaying, showUpNext } = this.state;
    const currentIndex = clips.findIndex((e) => e.id === selectedClipId);
    const previousIndex = currentIndex === 0 ? clips.length - 1 : currentIndex - 1;
    const previousClipId = clips[previousIndex];
    const shouldAutoplay = isPlaying || showUpNext;
    this.switchClip(
      previousClipId,
      shouldAutoplay,
      this.state.selectedClipOffsetTop - TRACK_SIZE_OFFSET,
    );
  };

  /**
   * Updates the Player config for customizations
   * Uses a passed window.postMessage `event` arg
   */
  updatePlayerConfig = (event) => {
    const { player, clips } = this.props;
    const { playlistData, hidePlaylistUI } = this.state;
    const {
      data: { type, payload },
    } = event;

    // todo: Unify settings for rle and albums? Trying to avoid breaking changes, so using separate event for RLE embeds.

    /*
     * Handling albums embed settings update event.
     */
    if (type === EMBED_SETTINGS.UPDATE_EMBED_SETTINGS && payload) {
      const newSettings = {};
      if ('hideVimeoLogo' in payload) {
        newSettings.logo = !payload.hideVimeoLogo;
      }
      if ('embedBrandColor' in payload) {
        // User might be turning color off/to default blue:
        if (payload.embedBrandColor === false) {
          this.props.player.color = '00adef';
        } else {
          if (payload.brandColor) {
            this.props.player.color = payload.brandColor;
          }
        }
      }
      if ('embedCustomLogo' in payload) {
        // User might be turning logo off:
        if (payload.embedCustomLogo === false) {
          newSettings.custom_logo = null;
        } else {
          if (payload.logoUrl) {
            newSettings.custom_logo = {
              img: payload.logoUrl,
              url: null,
            };
          }
        }
      }
      if ('showSchedule' in payload && playlistData.showSchedule !== payload.showSchedule) {
        this.setState((prevState) => ({
          playlistData: {
            ...playlistData,
            showSchedule: payload.showSchedule,
          },
        }));
      }
      if ('showPlaylist' in payload && hidePlaylistUI !== !payload.showPlaylist) {
        this.setState((prevState) => ({
          hidePlaylistUI: !payload.showPlaylist,
        }));
      }
      player._setEmbedSettings(newSettings);
    }

    /*
     * Handle RLE embed settings changes.
     * Embed settings constants should be mapped to player format.
     * Almost same settings as for common clip, does not include interaction tools and like/share/watch later buttons.
     * Will be applied for latest clip and embed playlist.
     */
    if (type === EMBED_SETTINGS.UPDATE_RLE_EMBED_SETTINGS && payload) {
      // Transform embed constants to player config naming.
      const {
        [EMBED_SETTINGS.COLOR]: color,
        [EMBED_SETTINGS.SHOW_SCHEDULE]: showSchedule,
        [EMBED_SETTINGS.SHOW_PLAYLIST]: showPlaylist,
        [EMBED_SETTINGS.SHOW_LATEST_ARCHIVED_CLIP]: showLatestArchivedClip,
        [EMBED_SETTINGS.SHOW_FULLSCREEN]: showFullscreen,
        [EMBED_SETTINGS.SHOW_VIMEO_LOGO]: showVimeoLogo,
        [EMBED_SETTINGS.SHOW_CUSTOM_LOGO]: showCustomLogo,
        [EMBED_SETTINGS.CUSTOM_LOGO_URL]: customLogoUrl,
        [EMBED_SETTINGS.CUSTOM_LOGO_LINK_URL]: customLogoLinkUrl,
        [EMBED_SETTINGS.CUSTOM_LOGO_USE_LINK]: customLogoUseLink,
        [EMBED_SETTINGS.STICKY_CUSTOM_LOGO]: customLogoSticky,
        [EMBED_SETTINGS.SHOW_EVENT_SCHEDULE]: eventSchedule,
        [EMBED_SETTINGS.HIDE_LIVE_LABEL]: hideLiveLabel,
        [EMBED_SETTINGS.COLOR_ONE]: colorOne,
        [EMBED_SETTINGS.COLOR_TWO]: colorTwo,
        [EMBED_SETTINGS.COLOR_THREE]: colorThree,
        ...newSettings
      } = payload;

      try {
        if (colorOne && colorOne !== `#${player.colorOne}`) {
          player.colorOne = colorOne;
        }
        if (colorTwo && colorTwo !== `#${player.colorTwo}`) {
          player.colorTwo = colorTwo;
        }
        if (colorThree && colorThree !== `#${player.colorThree}`) {
          player.colorThree = colorThree;
        }
        if (color && color !== `#${player.color}`) {
          player.color = color;
        }
      } catch (error) {
        // Ignore errors from contrast suggestions because it will fail other settings application.
      }

      newSettings[EMBED_SETTINGS.LOGO] = showVimeoLogo;
      newSettings[EMBED_SETTINGS.FULLSCREEN] = showFullscreen;

      // User might be turning logo off:
      if (showCustomLogo && customLogoUrl) {
        newSettings.custom_logo = {
          img: customLogoUrl,
          url: customLogoUseLink ? customLogoLinkUrl : '',
          useLink: customLogoUseLink,
          sticky: customLogoSticky,
        };
      } else {
        newSettings.custom_logo = null;
      }

      if (playlistData.showSchedule !== showSchedule) {
        this.setState({ playlistData: { ...playlistData, showSchedule } });
      }

      if (playlistData.showLatestArchivedClip !== showLatestArchivedClip) {
        const clipsCount = clips.length;
        const isLatestClipPlaceholder = clips.length > 0 && clips[0].isPending;
        const shouldShowArchivedClip =
          isLatestClipPlaceholder && clipsCount > 1 && showLatestArchivedClip;

        this.setState({
          // todo: Can we not override 'total'? Maybe just show clips length for PlaylistPlaybar.jsx? Ask code owners.
          playlistData: {
            ...playlistData,
            showLatestArchivedClip,
            total: shouldShowArchivedClip ? clipsCount - 1 : clipsCount,
          },
          clips: shouldShowArchivedClip ? clips.slice(1) : clips,
        });
      }

      if (hidePlaylistUI !== !showPlaylist) {
        this.setState({ hidePlaylistUI: !showPlaylist });
      }

      player._setEmbedSettings(newSettings);

      const newLiveSettings = {
        [EMBED_SETTINGS.SHOW_EVENT_SCHEDULE]: eventSchedule,
        [EMBED_SETTINGS.HIDE_LIVE_LABEL]: hideLiveLabel,
      };

      player._setLiveEventSettings(newLiveSettings);
    }
  };

  render = (props, state) => {
    return props.render({
      addNewLiveClip: this.addNewLiveClip,
      playlistData: state.playlistData,
      clips: state.clips,
      currentLiveViewers: state.currentLiveViewers,
      headerRef: (headerRef) => (this.headerRef = headerRef),
      hide: this.hide,
      hideTracklist: this.hideTracklist,
      isHovered: state.isHovered,
      isLive: state.isLive,
      isLoading: state.isLoading,
      isMobile: state.isMobile,
      isPlaying: state.isPlaying,
      isShowing: state.isShowing,
      isTiny: state.isTiny,
      nextClip: this.nextClip,
      nextLiveClip: state.nextLiveClip,
      onMouseEnter: this.onMouseEnter,
      onMouseLeave: this.onMouseLeave,
      onResize: this.onResize,
      previousClip: this.previousClip,
      selectedClipId: state.selectedClipId,
      show: this.show,
      showUpNext: state.showUpNext,
      switchClip: this.switchClip,
      toggleShowing: this.toggleShowing,
      toggleTracklist: this.toggleTracklist,
      tracklistExpanded: state.tracklistExpanded,
      tracklistRef: (tracklistRef) => (this.tracklistRef = tracklistRef),
      upNextClip: state.upNextClip,
      hidePlaylistUI: state.hidePlaylistUI,
    });
  };
}

export default PlaylistContainer;
